import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import {
  DEFAULT_PAGE_EVENT,
  DEFAULT_PAGE_SIZE_OPTIONS,
  DEFAULT_SORT_STATE,
  defaultSortState,
  getPaginatedData,
} from '../../../utils/table-utils';
import { ContractNumberSearchModel } from '../../../models/ContractNumberSearchModel';
import { RfxMetaDataRequest } from '../../../models/RfxMetaDataRequest';
import {
  ConversionXrefModel,
  XrefRelationshipModel,
} from '../../../models/XrefRelationshipModel';
import { RequestService } from '../../../services/request/request.service';
import {
  IncumbentDropdownOptions,
  IncumbentCriteriaResult,
} from '../../dialogs/request-target-search/request-target-search.component';
import { ProductSourceTypeModel } from '../../../models/ProductSourceTypeModel';
import { ConversionSelectionDialogComponent } from '../../dialogs/conversion-selection-dialog/conversion-selection-dialog.component';
import {
  EmailTemplateData,
  EmailTemplateDialogComponent,
  EmailTemplateResult,
} from '../../dialogs/email-template-dialog/email-template-dialog.component';
import { VccSearchModel } from '../../dialogs/request-target-search/vcc-request-search-modal/vcc-request-search-modal.component';

interface XrefIncumbentProductRow extends XrefRelationshipModel {
  isXrefRow?: boolean;
}

export interface ConversionTarget {
  sourceType: ProductSourceTypeModel;
  sourceId: number;
}

enum RfxMonthFilterOptions {
  NONE = 0,
  SIX_MONTHS = 6,
  TWELVE_MONTHS = 12,
  EIGHTEEN_MONTHS = 18,
  TWENTYFOUR_MONTHS = 24,
}

@Component({
  selector: 'app-xref-list',
  templateUrl: './xref-list.component.html',
  styleUrls: ['./xref-list.component.scss'],
  standalone: false,
})
export class XrefListComponent {
  public rfxMonthFilterOptions = [
    { value: RfxMonthFilterOptions.NONE, viewValue: 'None' },
    {
      value: RfxMonthFilterOptions.SIX_MONTHS,
      viewValue: `Last ${RfxMonthFilterOptions.SIX_MONTHS} Months`,
    },
    {
      value: RfxMonthFilterOptions.TWELVE_MONTHS,
      viewValue: `Last ${RfxMonthFilterOptions.TWELVE_MONTHS} Months`,
    },
    {
      value: RfxMonthFilterOptions.EIGHTEEN_MONTHS,
      viewValue: `Last ${RfxMonthFilterOptions.EIGHTEEN_MONTHS} Months`,
    },
    {
      value: RfxMonthFilterOptions.TWENTYFOUR_MONTHS,
      viewValue: `Last ${RfxMonthFilterOptions.TWENTYFOUR_MONTHS} Months`,
    },
  ];

  public pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  public productColumns = [
    'supplierName',
    'catalogNumber',
    'description',
    'spendCoveragePercentage',
  ];
  public supplierVerificationColumns = ['supplierVerifiedDateTime'];

  public incumbentHeaders = [
    'expandColumn',
    'incumbentProducts',
    'conversionCheck',
  ];
  public incumbentColumns = [
    'expandIncumbent',
    ...this.productColumns,
    ...this.supplierVerificationColumns,
  ];
  public xrefColumns = [
    'spacerColumn',
    ...this.productColumns,
    ...this.supplierVerificationColumns,
  ];

  constructor(
    public requestService: RequestService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  private readonly _pageEvent$ = new BehaviorSubject<PageEvent>(
    DEFAULT_PAGE_EVENT,
  );
  private readonly _sortState$ = new BehaviorSubject<Sort>(DEFAULT_SORT_STATE);
  private readonly _expandedIncumbents$ = new BehaviorSubject<Set<number>>(
    new Set(),
  );

  private readonly _routeRfxHeaderKey$ = this.route.params.pipe(
    map(params => Number(params['rfxHeaderKey'])),
    distinctUntilChanged(),
  );

  private readonly _conversionModalSelection$ =
    new BehaviorSubject<IncumbentCriteriaResult | null>(null);
  private readonly _selectedConversionTarget$: Observable<ConversionTarget | null> =
    this._conversionModalSelection$.pipe(
      map(result => {
        if (!result) {
          return null;
        }

        switch (result.resultType) {
          case IncumbentDropdownOptions.VCC:
            return {
              sourceType: ProductSourceTypeModel.VCC,
              sourceId: (result.value as VccSearchModel).vccId,
            };
          case IncumbentDropdownOptions.CONTRACT_NUMBER:
            return {
              sourceType: ProductSourceTypeModel.CONTRACT,
              sourceId: (result.value as ContractNumberSearchModel).contractId,
            };
        }
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  protected readonly loadingConversions$ = new BehaviorSubject<boolean>(false);

  protected readonly rfxMetaData$ = combineLatest([
    this._routeRfxHeaderKey$,
    this._selectedConversionTarget$,
  ]).pipe(
    switchMap(([rfxHeaderKey, conversionTarget]) => {
      if (conversionTarget) {
        return this.requestService.getRfxMetaData({
          rfxHeaderKey,
          sourceType: conversionTarget.sourceType,
          sourceId: conversionTarget.sourceId,
        });
      } else {
        return this.requestService.getRfxMetaData({ rfxHeaderKey });
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly rfxMonthFilter$ =
    new BehaviorSubject<RfxMonthFilterOptions>(
      RfxMonthFilterOptions.TWELVE_MONTHS,
    );

  private readonly _incumbentProductsWithCrosses$ = combineLatest([
    this._routeRfxHeaderKey$,
    this._selectedConversionTarget$,
  ]).pipe(
    switchMap(([rfxHeaderKey, conversionTarget]) => {
      this._sortState$.next({
        active: 'supplierVerifiedDateTime',
        direction: 'desc',
      });
      this.loadingConversions$.next(true);

      if (conversionTarget) {
        return this.requestService.getRfxCrosses({
          rfxHeaderKey,
          sourceType: conversionTarget.sourceType,
          sourceId: conversionTarget.sourceId,
        });
      } else {
        return this.requestService.getRfxCrosses({ rfxHeaderKey });
      }
    }),
    tap(() => this.loadingConversions$.next(false)),
    startWith([] as XrefRelationshipModel[]),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly _incumbentProductsWithFilteredCrosses$ = combineLatest([
    this._incumbentProductsWithCrosses$,
    this.rfxMonthFilter$,
  ]).pipe(
    map(([incumbentProducts, rfxMonthFilter]) => {
      this._expandedIncumbents$.next(new Set());
      return this.filterIncumbentsByRfxMonth(incumbentProducts, rfxMonthFilter);
    }),
  );

  protected readonly vm$ = combineLatest([
    this.rfxMetaData$,
    this._routeRfxHeaderKey$,
    this._incumbentProductsWithFilteredCrosses$,
    this._expandedIncumbents$,
    this._sortState$,
    this._pageEvent$,
    this._selectedConversionTarget$,
    this.rfxMonthFilter$,
  ]).pipe(
    map(
      ([
        metadata,
        rfxHeaderKey,
        incumbentProducts,
        expandedIncumbents,
        sortState,
        pageEvent,
        selectedConversionTarget,
      ]) => {
        const sortedProducts = this.sortProducts(incumbentProducts, sortState);
        pageEvent.length = sortedProducts.length;

        const paginatedProducts = this.generateXrefRows(
          getPaginatedData(sortedProducts, pageEvent),
          expandedIncumbents,
        );

        const verifiedCrossesCount = incumbentProducts.reduce(
          (count, product) => {
            return count + (this.hasSupplierProvidedCross(product) ? 1 : 0);
          },
          0,
        );

        const totalSpendCoverage = incumbentProducts
          .reduce((total, product) => {
            const spendCovered = this.hasSupplierProvidedCross(product)
              ? Number(product.spendCoveragePercentage)
              : 0;
            return total + spendCovered;
          }, 0)
          .toFixed(2);
        const tableFromString = this.formatTableFromString(metadata);
        const tableToString = this.formatTableToString(metadata);

        return {
          rfxHeaderKey,
          tableFromString,
          tableToString,
          incumbentProducts,
          paginatedProducts,
          pageEvent,
          selectedConversionTarget,
          verifiedCrossesCount,
          totalSpendCoverage,
        };
      },
    ),
  );

  formatTableFromString(metadata: RfxMetaDataRequest) {
    switch (metadata.incumbentSourceType) {
      case ProductSourceTypeModel.CONTRACT:
        return `From Contract ID: ${metadata.incumbentDisplayValue}`;
      case ProductSourceTypeModel.VCC:
        return `From VCC: ${metadata.incumbentDisplayValue}`;
      default:
        return '';
    }
  }

  formatTableToString(metadata: RfxMetaDataRequest) {
    if (!metadata.competingSourceType) {
      return '';
    }

    switch (metadata.competingSourceType) {
      case ProductSourceTypeModel.CONTRACT:
        return `To Contract ID: ${metadata.competingDisplayValue}`;
      case ProductSourceTypeModel.VCC:
        return `To VCC: ${metadata.competingDisplayValue}`;
      default:
        return '';
    }
  }

  navigateToMyRequests() {
    this.router.navigate(['/request']);
  }

  handlePageEvent(pageEvent: PageEvent) {
    this._pageEvent$.next(pageEvent);
  }

  handleSortEvent(sortState: Sort) {
    this._sortState$.next(sortState);
  }

  hasXrefTargets(product: XrefRelationshipModel): boolean {
    return product.xrefTargets && product.xrefTargets.length > 0;
  }

  isIncumbentExpanded({ incumbent }: XrefRelationshipModel): boolean {
    return this._expandedIncumbents$.value.has(incumbent.pimKey);
  }

  isIncumbentRow(_index: number, product: XrefIncumbentProductRow): boolean {
    return !product.isXrefRow;
  }

  isXrefRow(_index: number, product: XrefIncumbentProductRow): boolean {
    return !!product.isXrefRow;
  }

  toggleRow(pimKey: number) {
    const expandedIncumbents = new Set(this._expandedIncumbents$.value);

    if (expandedIncumbents.has(pimKey)) {
      expandedIncumbents.delete(pimKey);
    } else {
      expandedIncumbents.add(pimKey);
    }

    this._expandedIncumbents$.next(expandedIncumbents);
  }

  formatSpendCoverage(spendCoverage: string | number) {
    if (!spendCoverage) return '';

    const spendCoverageNum = Number(spendCoverage).toFixed(2);
    if (spendCoverageNum === `0.00`) return '~0.00%';

    return spendCoverageNum + '%';
  }

  private generateXrefRows(
    incumbentProductsModel: XrefRelationshipModel[],
    expandedIncumbents: Set<number>,
  ): XrefIncumbentProductRow[] {
    const productsWithXrefRow: XrefIncumbentProductRow[] = [];

    incumbentProductsModel.forEach(product => {
      productsWithXrefRow.push(product);

      // Adds the Xref Row for Mat-Table if the Incumbent is Expanded
      if (expandedIncumbents.has(product.incumbent.pimKey)) {
        productsWithXrefRow.push({
          ...product,
          isXrefRow: true,
        });
      }
    });

    return productsWithXrefRow;
  }

  private filterIncumbentsByRfxMonth(
    incumbentProducts: XrefRelationshipModel[],
    rfxMonthFilter: RfxMonthFilterOptions,
  ): XrefRelationshipModel[] {
    if (rfxMonthFilter === RfxMonthFilterOptions.NONE) {
      return incumbentProducts;
    }

    const filterMonthDate = new Date();
    filterMonthDate.setMonth(
      filterMonthDate.getMonth() - rfxMonthFilter,
      filterMonthDate.getDate(),
    );

    const filteredIncumbents = incumbentProducts.map(incumbentProduct => {
      const filteredXrefTargets = incumbentProduct.xrefTargets?.filter(xref => {
        const supplierVerifiedDate = new Date(xref.supplierVerifiedDateTime);
        return supplierVerifiedDate.getTime() >= filterMonthDate.getTime();
      });

      return { ...incumbentProduct, xrefTargets: filteredXrefTargets };
    });
    return filteredIncumbents;
  }

  private sortProducts(products: XrefRelationshipModel[], sortState: Sort) {
    if (!sortState.active || sortState.direction === '') {
      return products;
    } else {
      const sortedProducts = products.sort((a, b) => {
        switch (sortState.active) {
          case 'supplierVerifiedDateTime':
            return this.sortSupplierVerified(a, b, sortState);
          case 'spendCoveragePercentage':
            return this.sortSpendCoverage(a, b, sortState);
          default:
            return defaultSortState(a.incumbent, b.incumbent, sortState);
        }
      });

      sortedProducts.forEach(product => {
        return this.sortXrefTargets(product.xrefTargets ?? [], sortState);
      });

      return sortedProducts;
    }
  }

  private sortSupplierVerified(
    a: XrefRelationshipModel,
    b: XrefRelationshipModel,
    sortState: Sort,
  ) {
    const aXrefTargets = a.xrefTargets?.length ?? 0;
    const bXrefTargets = b.xrefTargets?.length ?? 0;
    const aHasSupplierOffset = this.hasSupplierProvidedCross(a)
      ? Math.max(bXrefTargets, aXrefTargets)
      : 0;
    const bHasSupplierOffset = this.hasSupplierProvidedCross(b)
      ? Math.max(aXrefTargets, bXrefTargets)
      : 0;

    const aSortValue = aXrefTargets + aHasSupplierOffset;
    const bSortValue = bXrefTargets + bHasSupplierOffset;

    return sortState.direction === 'asc'
      ? aSortValue - bSortValue
      : bSortValue - aSortValue;
  }

  private sortSpendCoverage(
    a: XrefRelationshipModel,
    b: XrefRelationshipModel,
    sortState: Sort,
  ) {
    const aSpend = Number(a.spendCoveragePercentage) ?? 0;
    const bSpend = Number(b.spendCoveragePercentage) ?? 0;

    return sortState.direction === 'asc' ? aSpend - bSpend : bSpend - aSpend;
  }

  private sortXrefTargets(xrefTargets: ConversionXrefModel[], sortState: Sort) {
    return xrefTargets.sort((a, b) => {
      if (sortState.active === 'isSupplierVerified') {
        const aDateValue = new Date(a.supplierVerifiedDateTime ?? 0).getTime();
        const bDateValue = new Date(b.supplierVerifiedDateTime ?? 0).getTime();

        return sortState.direction === 'asc'
          ? aDateValue - bDateValue
          : bDateValue - aDateValue;
      } else {
        return defaultSortState(a, b, sortState);
      }
    });
  }

  openSelectConversionDialog() {
    this.dialog
      .open(ConversionSelectionDialogComponent, {
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result: IncumbentCriteriaResult | null) => {
        if (!result) {
          return;
        }

        this._conversionModalSelection$.next(result);
      });
  }

  disableDownloadBtn(
    rfxMetaData: RfxMetaDataRequest,
    incumbentProducts: XrefRelationshipModel[],
  ) {
    return incumbentProducts.length === 0 || !rfxMetaData.competingSourceType;
  }

  openEmailTemplate(
    rfxHeaderKey: number,
    rfxMetaData: RfxMetaDataRequest,
    selectedConversionTarget: ConversionTarget | null,
    incompleteProductCount: number,
    products: XrefRelationshipModel[],
  ) {
    const spendSortedProducts = this.sortProducts(products, {
      active: 'spendCoveragePercentage',
      direction: 'asc',
    });

    this.dialog
      .open<
        EmailTemplateDialogComponent,
        EmailTemplateData,
        EmailTemplateResult
      >(EmailTemplateDialogComponent, {
        data: {
          rfxHeaderKey,
          rfxMetaData,
          selectedConversionTarget,
          incompleteProductCount,
          products: spendSortedProducts,
          monthsOldVerificationToKeep: this.rfxMonthFilter$.value,
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result && result.success && result.rfxHeaderKey != rfxHeaderKey) {
          this.router.navigateByUrl(
            this.router.url.replace(
              `/${rfxHeaderKey}`,
              `/${result.rfxHeaderKey}`,
            ),
            { skipLocationChange: true },
          );
        }
      });
  }

  private hasSupplierProvidedCross(product: XrefRelationshipModel) {
    return product.xrefTargets && product.xrefTargets.length > 0;
  }
}
